import { useEffect, useRef, useState } from "react";

// transform shape: { x: number (px), y: number (px), scale: number }
export const IDENTITY_TRANSFORM = { x: 0, y: 0, scale: 1 };
export const MIN_SCALE = 1;
export const MAX_SCALE = 4;

const clampScale = (s) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));

// Convert a client point to element-local coords relative to the element center.
const toLocal = (el, clientX, clientY) => {
  const rect = el.getBoundingClientRect();
  return {
    x: clientX - (rect.left + rect.width / 2),
    y: clientY - (rect.top + rect.height / 2),
  };
};

// Scale `cur` about a point `p` (element-local px, relative to center) while
// keeping that point visually fixed: t1 = p - (p - t0) * (s1 / s0). With
// transform-origin: center and translate measured from center this is exact.
const zoomAround = (cur, nextScaleRaw, p, emit) => {
  const s1 = clampScale(nextScaleRaw);
  const ratio = s1 / cur.scale;
  emit({
    scale: s1,
    x: p.x - (p.x - cur.x) * ratio,
    y: p.y - (p.y - cur.y) * ratio,
  });
};

// A controlled, freely draggable/zoomable photo used to manually align two
// progress photos shot from a different camera position/distance. The whole
// (uncropped) image is shown via object-contain and moves as one layer; the
// frame around it just clips the overflow. It owns no transform state: the
// parent passes `transform` and receives `onChange(next)`.
//
// Gestures (pan/pinch via pointer events for unified mouse/touch/pen handling;
// zoom-toward-cursor via a native non-passive wheel listener):
//   - one pointer  -> pan
//   - two pointers -> pinch zoom around the midpoint
//   - wheel        -> zoom toward the cursor
const PannablePhoto = ({
  src,
  alt,
  transform,
  onChange,
  className = "",
  style,
}) => {
  const ref = useRef(null);
  // pointerId -> { x, y } in client coords for every active pointer.
  const pointers = useRef(new Map());
  // Snapshot taken when a gesture starts, so moves are computed against it.
  const gesture = useRef(null);
  const [grabbing, setGrabbing] = useState(false);

  const t = transform || IDENTITY_TRANSFORM;
  // Latest transform/onChange for the native wheel listener (attached once).
  const latest = useRef({ t, onChange });
  latest.current = { t, onChange };

  const startGesture = () => {
    const pts = [...pointers.current.values()];
    if (pts.length === 1) {
      gesture.current = {
        mode: "pan",
        startX: t.x,
        startY: t.y,
        pointerX: pts[0].x,
        pointerY: pts[0].y,
      };
    } else if (pts.length === 2) {
      const dx = pts[0].x - pts[1].x;
      const dy = pts[0].y - pts[1].y;
      gesture.current = {
        mode: "pinch",
        startX: t.x,
        startY: t.y,
        startScale: t.scale,
        startDist: Math.hypot(dx, dy) || 1,
        // Pinch midpoint in element-local coords.
        mid: toLocal(
          ref.current,
          (pts[0].x + pts[1].x) / 2,
          (pts[0].y + pts[1].y) / 2
        ),
      };
    }
  };

  const onPointerDown = (e) => {
    ref.current.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    setGrabbing(true);
    startGesture();
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    if (!g) return;

    if (g.mode === "pan") {
      onChange({
        ...t,
        x: g.startX + (e.clientX - g.pointerX),
        y: g.startY + (e.clientY - g.pointerY),
      });
    } else if (g.mode === "pinch") {
      const pts = [...pointers.current.values()];
      if (pts.length < 2) return;
      const dx = pts[0].x - pts[1].x;
      const dy = pts[0].y - pts[1].y;
      const dist = Math.hypot(dx, dy) || 1;
      // Anchor to the gesture-start transform so repeated moves don't drift.
      zoomAround(
        { x: g.startX, y: g.startY, scale: g.startScale },
        g.startScale * (dist / g.startDist),
        g.mid,
        onChange
      );
    }
  };

  const endPointer = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size === 0) {
      gesture.current = null;
      setGrabbing(false);
    } else {
      // A finger lifted mid-pinch: re-snapshot for the remaining pointer(s).
      startGesture();
    }
  };

  // Native, non-passive wheel listener: React's onWheel is passive in React 18,
  // so preventDefault() there is ignored and the page would scroll while zooming.
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const { t: cur, onChange: emit } = latest.current;
      const p = toLocal(el, e.clientX, e.clientY);
      // ~12% per notch; deltaY < 0 (wheel up) zooms in.
      zoomAround(cur, cur.scale * (1 - e.deltaY * 0.0012), p, emit);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  return (
    <div
      ref={ref}
      className={`pannable ${grabbing ? "is-grabbing" : ""} ${className}`.trim()}
      style={style}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        style={{ transform: `translate(${t.x}px, ${t.y}px) scale(${t.scale})` }}
      />
    </div>
  );
};

export default PannablePhoto;
